import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { useStore } from "@/store";
import { useUser } from "@/contexts/UserContext";
import type { StoreApi } from "zustand";

/**
 * Context for providing an adapted store to main app components
 * This allows any component from the main app to work in Rapid by adapting the store API
 */
const MainAppStoreContext = createContext<any>(null);

interface MainAppStoreProviderProps {
    children: ReactNode;
}

/**
 * Provider that adapts Rapid's store to match the main app's global store API
 * This enables main app components to work seamlessly in Rapid without modifications
 *
 * The adapter maps Rapid's Zustand store structure to match the main app's expected API:
 * - user (from UserContext)
 * - currentWorkspaceId (from Rapid's currentWorkspace)
 * - getWorkspace() (from Rapid's getCurrentWorkspace)
 * - getUsage() (from Rapid's getCurrentWorkspaceUsage)
 *
 * @example
 * ```tsx
 * import { MainAppStoreProvider } from '@/adapters/MainAppStoreAdapter';
 * import { StoreProvider } from '@scripts/../state/StoreContext';
 * import { SomeMainAppComponent } from '@scripts/react/components/...';
 *
 * function MyRapidComponent() {
 *   return (
 *     <MainAppStoreProvider>
 *       <StoreProvider store={useMainAppStore()}>
 *         <SomeMainAppComponent />
 *       </StoreProvider>
 *     </MainAppStoreProvider>
 *   );
 * }
 * ```
 */
export function MainAppStoreProvider({ children }: MainAppStoreProviderProps) {
    const currentWorkspace = useStore((state) => state.currentWorkspace);
    const currentWorkspaceUsage = useStore((state) => state.getCurrentWorkspaceUsage());
    const user = useUser();
    const paidPlanSubscriptionById = useStore((state) => state.paidPlanSubscriptionById);
    const fetchPaidPlanSubscription = useStore((state) => state.fetchPaidPlanSubscription);

    // Create adapted store that mimics main app's globalStore API
    const adaptedStore = useMemo(() => {
        // Create a Zustand-like store API that matches main app's expectations
        const store: Partial<StoreApi<any>> = {
            getState: () => ({
                // User data - used by components that need authentication info
                user: user || {},

                // Workspace ID - used by hooks like usePaidPlanSubscription
                currentWorkspaceId: currentWorkspace?.id || null,

                // Workspace methods - used by components that need workspace/usage data
                getWorkspace: () => currentWorkspace,
                getUsage: () => currentWorkspaceUsage,

                // Subscription state - used by billing/upgrade components
                paidPlanSubscriptionById,
                fetchPaidPlanSubscription
            }),

            // Zustand's subscribe method - required for useStore hook to work
            subscribe: (listener: any) => {
                // Subscribe to Rapid store and propagate changes to main app components
                return useStore.subscribe((state) => {
                    // Notify listener about state changes
                    listener();
                });
            }
        };

        return store;
    }, [
        currentWorkspace,
        currentWorkspaceUsage,
        user,
        paidPlanSubscriptionById,
        fetchPaidPlanSubscription
    ]);

    return (
        <MainAppStoreContext.Provider value={adaptedStore}>{children}</MainAppStoreContext.Provider>
    );
}

/**
 * Hook to access the adapted main app store
 * This should be used with the StoreProvider from the main app to inject the adapted store
 *
 * @example
 * ```tsx
 * import { StoreProvider } from '@scripts/../state/StoreContext';
 * import { useMainAppStore } from '@/adapters/MainAppStoreAdapter';
 *
 * function Wrapper() {
 *   const adaptedStore = useMainAppStore();
 *   return (
 *     <StoreProvider store={adaptedStore}>
 *       <MainAppComponent />
 *     </StoreProvider>
 *   );
 * }
 * ```
 */
export function useMainAppStore() {
    const store = useContext(MainAppStoreContext);
    if (!store) {
        throw new Error("useMainAppStore must be used within a MainAppStoreProvider");
    }
    return store;
}

// Legacy exports for backward compatibility with existing code
// These will be removed in a future refactor
export const UpgradeModalStoreProvider = MainAppStoreProvider;
export const useAdaptedStore = useMainAppStore;
